import React, { Component } from 'react';
import { bool, string, object } from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import { FormattedMessage } from 'react-intl';
import get from 'lodash.get';

import storage from 'dibs-browser-storage';

import Dropdown, { alignConstants } from '../Dropdown/Dropdown';
import DropdownHeader from '../Dropdown/DropdownHeader';
import DropdownBody from '../Dropdown/DropdownBody';
import NotificationsIcon from './NotificationsIcon';
import NotificationsEmpty from './NotificationsEmpty';
import NotificationsList from './NotificationsList';
import NotificationsSpinner from './NotificationsSpinner';
import { setPromoCodeNotificationShown, wasPromoCodeNotificationShown } from '../../utils/promoCode.es';

import styles from './Notifications-style.scss';

const BATCH_SIZE = 5;
const LOCAL_STORAGE_KEY_NAME = "NAV_NOTIFICATIONS_DROPDOWN_LAST_OPEN_TIME";
const VIEW_ALL_LINK_URL = "/my/conversations/?filter=showUnreadOnly";

const resetState = () => ({
    isOpen: false,
    fetchingNotifications: true,
    fetchingMore: false,
    first: BATCH_SIZE
});
const loadMore = previousFirst => ({ first: previousFirst + BATCH_SIZE, fetchingMore: true});
const stopFetching = (isMore = false) => {
    const defaultReturn = { fetchingNotifications: false };
    return (isMore)
        ? Object.assign({}, defaultReturn, { fetchingMore: false })
        : defaultReturn;
};
const startFetching = () => ({ fetchingNotifications: true });
const openDropdown = () => ({ isOpen: true });

class Notifications extends Component {
    constructor(props) {
        super(props);
        this.state = resetState();

        this.loadNotifications = this.loadNotifications.bind(this);
        this.onMouseEnterIcon = this.onMouseEnterIcon.bind(this);
    }

    loadNotifications(isInitial = false) {
        const { localStorage } = storage;
        const { hasRootPendingRefetch, relay, userId, user } = this.props;
        const { fetchingMore, first } = this.state;
        const fetchNotificationVariables = { fetchNotifications: true, userId, hasUserId: !!userId };

        if (hasRootPendingRefetch) {
            return;
        }

        if (isInitial) {
            const refetchVariables = fragmentVariables => Object.assign({}, fragmentVariables, fetchNotificationVariables);
            const refetchCallback = () => {
                localStorage.setItem(LOCAL_STORAGE_KEY_NAME, Date.now());
                if (get(user, 'isPromoCodeEligible')) {
                    setPromoCodeNotificationShown();
                }
                this.setState(stopFetching);
            };

            this.setState(startFetching, () => relay.refetch(refetchVariables, null, refetchCallback, {force: true}));
        } else if (!fetchingMore) {
            const refetchVariables = fragmentVariables => Object.assign(
                {}, fragmentVariables, { first: first + BATCH_SIZE }, fetchNotificationVariables
            );
            const refetchCallback = () => this.setState(stopFetching(true));
            this.setState(loadMore(first), () => relay.refetch(refetchVariables, null, refetchCallback));
        }
    }

    onMouseEnterIcon(e) {
        e.stopPropagation();
        if (!this.state.isOpen) {
            this.loadNotifications(true);
            this.setState(openDropdown);
        }
    }

    render() {
        const { localStorage } = storage;
        const { hasUserId, isClient, user } = this.props;
        const { fetchingNotifications } = this.state;
        const conversations = get(this.props, 'initialViewer.conversations');
        const hasConversations = !!conversations;
        const hasNextPage = get(this.props, 'viewer.conversations.pageInfo.hasNextPage');
        const isPromoCodeEligible = !!get(user, 'isPromoCodeEligible');

        if (!isClient || hasUserId && !hasConversations || !hasUserId) {
            return null;
        }

        const conversationEdges = get(this.props, 'viewer.conversations.edges');
        const lastUnreadMessageDate = get(this.props, 'initialViewer.conversations.metadata.lastUnreadMessageDate');

        const hasNotifications = (conversationEdges && conversationEdges.length > 0) || isPromoCodeEligible;
        const lastOpenedTime = Number(localStorage.getItem(LOCAL_STORAGE_KEY_NAME)) || 0;

        const mostRecentMessageReceivedAt = (lastUnreadMessageDate) ? new Date(lastUnreadMessageDate).getTime() : 0;

        const spinner = <NotificationsSpinner size="medium" isBlock />;
        const content = (
            hasNotifications ?
                <NotificationsList
                    conversationEdges={conversationEdges}
                    loadMore={this.loadNotifications}
                    hasMore={hasNextPage !== false}
                    isPromoCodeEligible={isPromoCodeEligible}
                /> :
                <NotificationsEmpty />
        );

        return (
            <Dropdown align={alignConstants.ALIGN_RIGHT} onHide={() => {this.setState(resetState);}}>
                <NotificationsIcon
                    onMouseEnter={this.onMouseEnterIcon}
                    hasUnread={(mostRecentMessageReceivedAt > lastOpenedTime) || (isPromoCodeEligible && !wasPromoCodeNotificationShown())}
                />
                <DropdownHeader
                    title={<FormattedMessage id='dbl.Header.Notifications.header_title' defaultMessage='Notifications'/>}
                    link={<FormattedMessage id='dbl.Header.Notifications.header_link' defaultMessage='View All'/>}
                    href={VIEW_ALL_LINK_URL}
                />
                <DropdownBody>
                    <div className={styles.contentContainer}>
                        <div className={styles.content}>{fetchingNotifications ? spinner : content}</div>
                    </div>
                </DropdownBody>
            </Dropdown>
        );
    }
}

Notifications.propTypes = {
    hasUserId: bool,
    hasRootPendingRefetch: bool,
    isClient: bool,
    relay: object,
    userId: string,
    viewer: object,
    user: object
};

export default createRefetchContainer(
    Notifications,
    {
        initialViewer: graphql.experimental`
            fragment Notifications_initialViewer on Viewer
            {
                conversations (filter: "showUnreadOnly") @include(if: $hasUserId) {
                    metadata {
                        lastUnreadMessageDate
                    }
                }
            }
        `,
        viewer: graphql.experimental`
            fragment Notifications_viewer on Viewer
            @argumentDefinitions(
                first: {type: "Int", defaultValue: 5},
                fetchNotifications: {type: "Boolean!", defaultValue: false}
            ) {
                conversations (filter: "showUnreadOnly", first: $first) @include(if: $fetchNotifications)  {
                    edges {
                        node {
                            serviceId
                            inboxImage
                            otherMember(currentUserId: $userId) {
                                seller {
                                    sellerProfile {
                                        company
                                    }
                                }
                            }
                            latestMessage
                            latestMessageReceivedAt
                        }
                    }
                    pageInfo {
                        hasNextPage
                    }
                }
            }
        `,
        user: graphql`
            fragment Notifications_user on User {
                isPromoCodeEligible(code: "SAVE15")
            }
        `
    },
    graphql.experimental`
        query NotificationsRefetchQuery(
            $first: Int
            $fetchNotifications: Boolean!
            $userId: String!
        ) {
            viewer {
                ...Notifications_viewer @arguments(
                    first: $first,
                    fetchNotifications: $fetchNotifications
                )
            }
        }
    `,
);



// WEBPACK FOOTER //
// ./src/components/Notifications/Notifications.jsx